/* eslint-disable statura/no-floating-point-money --
 * Canonical sleepover disturbance pay calculator. Every $-arithmetic line
 * is regulated under:
 *   - SCHADS Award MA000100 cl 25.7(d)(iv) — sleepover disturbance pay
 *     (PER_EVENT model — 1h minimum per disturbance event, OT rate)
 *   - Aged Care Award MA000018 cl 22.9(f) read with cl 22.9(g) — work
 *     directed during a sleepover (LESSER_OF_WINDOW model — paid time is
 *     the lesser of [sleepoverStart → end-of-last-work] vs
 *     [start-of-first-work → sleepoverEnd], rate selected per
 *     employment subtype)
 *
 * Only Aged Care + SCHADS support sleepover provisions. Nurses + HPSS do
 * not (residential nurses don't sleepover under the Nurses Award; HPSS
 * allied health workers don't sleepover). The shared utility lives in
 * packages/utils/src/award-engines/ for consistency with recall.ts +
 * shift-classifier.ts but is only consumed by Aged Care + SCHADS paths.
 */

/**
 * Phase 1E (Award Engine Integrity 2026-05) — shared disturbance pay
 * utility. Consumed by snapshotPayRun's per-engine dispatch when the
 * award_code is MA000018 (Aged Care) or MA000100 (SCHADS).
 *
 * Phase 1I.1b (2026-05-16) — STRUCTURAL REWRITE for Aged Care. The Phase
 * 1E shape reused the SCHADS per-event-minimum-at-OT model for both
 * awards, but the aged-care-legislation verification artifact
 * (`docs/operations/clause-verifications/2026-05-16-aged-care-legislation-aged-care-disturbance-subclause.md`)
 * found this is structurally non-faithful to MA000018 cl 22.9(f).
 *
 * Two distinct disturbance pay models are now supported via the
 * `DisturbanceRules.model` discriminator:
 *
 * ─── PER_EVENT (SCHADS MA000100 cl 25.7(d)(iv)) ─────────────────────────
 *
 *   1. 1 hour MINIMUM per disturbance event, paid at the worker's OT rate.
 *   2. Multiple disturbances within the same sleepover do NOT collapse —
 *      each gets its own independent 1h minimum.
 *   3. Above-minimum disturbance time is rounded UP to 15-minute
 *      increments (industry convention).
 *   4. OT rate is per-day-type — a disturbance crossing Saturday 23:59 →
 *      Sunday 00:01 pays at Sunday OT rate (dominant-day-type selection).
 *
 *   Input: PerEventDisturbanceInput (single event).
 *   Caller invokes ONCE per disturbance event; sums externally.
 *
 * ─── LESSER_OF_WINDOW (Aged Care MA000018 cl 22.9(f) + (g)) ─────────────
 *
 *   1. NO per-event minimum. Paid time is computed for the whole sleepover:
 *
 *      paidTime = min(
 *        sleepoverStart → max(workEnd over all work episodes),
 *        min(workStart over all work episodes) → sleepoverEnd
 *      )
 *
 *      Worked example (verbatim from MA000018 cl 22.9(f)):
 *        8h sleepover 22:00 → 06:00, single disturbance 02:00 → 02:15.
 *        Lesser of [22:00 → 02:15 = 4h 15m] and [02:00 → 06:00 = 4h] = 4h.
 *
 *   2. Multiple disturbances within one sleepover COLLAPSE by construction.
 *      Two 30-min disturbances at 23:00 and 04:00 in an 8h sleepover:
 *        Lesser of [22:00 → 04:30 = 6h 30m] and [23:00 → 06:00 = 7h] = 6h 30m.
 *
 *   3. NO rounding (the Award text uses raw time terms).
 *
 *   4. Rate selection per cl 22.9(g):
 *      - FULL_TIME (cl 22.9(g)(i)): OT rate unconditionally.
 *      - PART_TIME (cl 22.9(g)(ii)): ordinary pay + applicable shift &
 *        weekend penalties; OT applies ONLY when the caller-supplied
 *        `pptOvertimeApplies` flag is true (caller computes the
 *        cross-worker / per-day / weekly / fortnightly threshold).
 *      - CASUAL (cl 22.9(g)(iii)): ordinary pay + applicable shift &
 *        weekend penalties + 25% casual loading; OT applies ONLY when
 *        the caller-supplied `casualOvertimeApplies` flag is true (caller
 *        computes the 38h/week or 76h/fortnight threshold).
 *
 *      Phase 1I.1b simplification: the "applicable shift" interaction is
 *      pending separate clause verification — for now the engine applies
 *      day-of-week penalties (Saturday/Sunday/PH) but NOT shift loadings
 *      (afternoon/night). This is conservative-correct in the typical
 *      case and is documented for follow-up (Phase 2 clause-verification
 *      sweep TodoList).
 *
 *   5. The flat SLEEPOVER allowance (cl 22.9(d), 5.2% of standard rate,
 *      $62.35/night as at 1 July 2025) is paid ADDITIVELY by the caller
 *      independently of this calculator. The cl 22.9(f) work-during-
 *      sleepover pay is "in addition to" the sleepover allowance.
 *
 *   6. cl 22.9(j) failsafe (8 consecutive hours off → double the
 *      appropriate rate if employer makes the worker resume without that
 *      rest) is a SEPARATE engine pathway not encoded here. Tracked as
 *      a distinct workstream — see task #46.
 *
 *   Input: LesserOfWindowDisturbanceInput (full sleepover context).
 *   Caller invokes ONCE per sleepover, passing all disturbances together.
 *
 * See:
 *   - `award-engine-integrity-architecture-2026-05-15.md` §1.5
 *   - SCHADS MA000100 cl 25.7(d)(iv)
 *   - Aged Care MA000018 cl 22.9(f) + cl 22.9(g) + cl 22.9(d) (allowance)
 *   - `docs/operations/clause-verifications/2026-05-16-aged-care-legislation-aged-care-disturbance-subclause.md`
 */

import {
  coverageGapsField,
  resolveRequiredPenalty,
  type EbaCoverageGap,
  type PenaltyBand,
  type RateContext,
} from '../award-rate-lookup';
import { roundMoney } from '../money-rounding';
import type { AwardCode } from './types';

export type DisturbancePayBand = 'OT_WEEKDAY' | 'OT_SATURDAY' | 'OT_SUNDAY' | 'OT_PUBLIC_HOLIDAY';

export type DisturbanceModel = 'PER_EVENT' | 'LESSER_OF_WINDOW';

/** Employment subtype for cl 22.9(g) rate selection (LESSER_OF_WINDOW only). */
export type DisturbanceEmploymentSubtype = 'FULL_TIME' | 'PART_TIME' | 'CASUAL';

/**
 * PER_EVENT input shape (SCHADS). One disturbance per call; caller sums
 * externally for multiple disturbances within a sleepover.
 */
export interface PerEventDisturbanceInput {
  model?: 'PER_EVENT';
  /** When the disturbance started. */
  disturbanceStartedAt: Date;
  /** When the disturbance ended. */
  disturbanceEndedAt: Date;
  /** IANA timezone for day-of-week selection. Defaults to Australia/Sydney. */
  timezone?: string;
  /** True if the disturbance fell on a declared public holiday. */
  isPublicHoliday?: boolean;
}

/**
 * LESSER_OF_WINDOW input shape (Aged Care MA000018 cl 22.9(f)).
 *
 * The calculator needs:
 *   - The full sleepover boundaries (from the parent timesheet);
 *   - The complete set of non-emergency work episodes performed during
 *     the sleepover.
 *
 * Caller invokes ONCE per sleepover. If there are no work episodes, the
 * worker is paid only the cl 22.9(d) sleepover allowance (handled by the
 * caller, not this calculator) and this function should not be invoked.
 */
export interface LesserOfWindowDisturbanceInput {
  model: 'LESSER_OF_WINDOW';
  /** Sleepover start (from the parent timesheet). */
  sleepoverStart: Date;
  /** Sleepover end (from the parent timesheet). */
  sleepoverEnd: Date;
  /**
   * Every non-emergency work episode performed during the sleepover.
   * Order is not significant; the calculator uses min(startedAt) and
   * max(endedAt) across the array. At least one episode is required —
   * invoking with an empty array returns zero pay with a warning.
   */
  workEpisodes: Array<{ startedAt: Date; endedAt: Date }>;
  /** IANA timezone for day-of-week selection. Defaults to Australia/Sydney. */
  timezone?: string;
  /** True if the sleepover fell on a declared public holiday. */
  isPublicHoliday?: boolean;
}

export type DisturbancePayInput = PerEventDisturbanceInput | LesserOfWindowDisturbanceInput;

export interface DisturbanceRules {
  awardCode: AwardCode;
  /** Phase 1I.1b — which structural model applies. */
  model: DisturbanceModel;
  /**
   * Minimum payment per disturbance event, in hours. SCHADS = 1.0. Aged
   * Care LESSER_OF_WINDOW: NO Award-text minimum, this field is ignored.
   */
  minimumHours: number;
  /**
   * Round-up increment for above-minimum disturbance time (PER_EVENT only).
   * Industry default 15min. LESSER_OF_WINDOW model: NO Award-text rounding,
   * this field is ignored.
   */
  roundingMinutes: number;
  /**
   * Casual loading treatment — both awards are INCLUSIVE at the band level
   * (penalty multipliers in `award_rate_versions` already embed 25%). For
   * Aged Care LESSER_OF_WINDOW the casual-loading semantics are documented
   * per cl 22.9(g)(iii): ordinary + shift/weekend penalty + 25% casual
   * loading, which the band-level penalty already encodes when the
   * `employmentType=CASUAL` PenaltyLine is seeded.
   */
  casualLoadingTreatment: 'INCLUSIVE';
  /** Quoted in the breakdown for audit. */
  clauseRef: string;
}

export interface DisturbancePayBreakdown {
  /** Paid hours after model-specific computation. */
  paidHours: number;
  /** Dollar pay before any pay-run-level deductions. */
  paidAmount: number;
  /** Day-type band that selected the rate multiplier. */
  bandApplied: DisturbancePayBand;
  /** Rate multiplier applied. */
  multiplierApplied: number;
  /** Award clause citation. */
  clauseRef: string;
  /**
   * For PER_EVENT: true if the single disturbance crossed a day-type boundary.
   * For LESSER_OF_WINDOW: true if the paid window crossed a day-type boundary.
   * Dominant day-type by paid minutes drives the band selection in both cases.
   */
  spansDayBoundary: boolean;
  /**
   * Audit string explaining the rate selection. LESSER_OF_WINDOW only.
   * Example: 'cl 22.9(g)(i) FT → OT', 'cl 22.9(g)(ii) PPT → ordinary + Sat penalty'.
   */
  rateRationale?: string;
  /**
   * LESSER_OF_WINDOW only — number of distinct work episodes that
   * collapsed into the single paid window. >1 indicates multi-disturbance.
   */
  workEpisodeCount?: number;
  /**
   * LESSER_OF_WINDOW only — the model the engine ran. Useful for audit
   * panels distinguishing Aged Care from SCHADS rows.
   */
  model: DisturbanceModel;
  /** EBA strict-mode gaps that make this disturbance non-persistable. */
  coverageGaps?: EbaCoverageGap[];
  warnings: string[];
}

/**
 * Options for the LESSER_OF_WINDOW path (cl 22.9(g) employment-subtype
 * rate selection). Ignored on PER_EVENT path.
 */
export interface DisturbancePayOptions {
  /**
   * MA000018 cl 22.9(g) rate-selection input. When omitted on the
   * LESSER_OF_WINDOW path, the engine falls back to PERMANENT → FT (which
   * may over-pay PPT workers). The caller (snapshotPayRun) should always
   * supply this for Aged Care.
   */
  employmentSubtype?: DisturbanceEmploymentSubtype;
  /**
   * Caller-determined: does cl 22.9(g)(ii) PPT-overtime threshold apply
   * for this sleepover? Triggered by hours-on-day exceeding FT hours, or
   * 11h where no FT employees, or 38h/week, or 76h/fortnight. The caller
   * computes this from the full timesheet/roster context; the engine
   * accepts the result. Defaults to false.
   */
  pptOvertimeApplies?: boolean;
  /**
   * Caller-determined: does cl 22.9(g)(iii) casual-overtime threshold
   * apply for this sleepover? Triggered by 38h/week or 76h/fortnight
   * exceedance. Defaults to false.
   */
  casualOvertimeApplies?: boolean;
}

// Phase 1.4.5 (2026-05-17): per-award DisturbanceRules instances moved to
// `award-data/schads.ts` (SCHADS_DISTURBANCE_RULES) and `award-data/aged-care.ts`
// (AGED_CARE_DISTURBANCE_RULES) per the Option-1 SSOT decision. Re-exported here
// so existing consumers (e.g. `award-engines/index.ts` barrel) keep working.
// The `DisturbanceRules` interface + `calculateDisturbancePay` function remain
// in this file as the engine's structural contract.
export { SCHADS_DISTURBANCE_RULES } from '../award-data/schads';
export { AGED_CARE_DISTURBANCE_RULES } from '../award-data/aged-care';

/* ═══════════════════════════════════════════════════════════════════════
 * Internal helpers
 * ═══════════════════════════════════════════════════════════════════════ */

function getDayOfWeek(d: Date, tz: string): number {
  const fmt = new Intl.DateTimeFormat('en-AU', { timeZone: tz, weekday: 'short' });
  const short = fmt.format(d);
  return ({ Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 } as Record<string, number>)[
    short
  ]!;
}

function selectBand(
  start: Date,
  end: Date,
  tz: string,
  isPublicHoliday: boolean,
): {
  band: DisturbancePayBand;
  spansDayBoundary: boolean;
} {
  if (isPublicHoliday) return { band: 'OT_PUBLIC_HOLIDAY', spansDayBoundary: false };

  const startDow = getDayOfWeek(start, tz);
  const endDow = getDayOfWeek(end, tz);
  const spansDayBoundary = startDow !== endDow;

  let dominantDow = startDow;
  if (spansDayBoundary) {
    const startFmt = new Intl.DateTimeFormat('en-AU', {
      timeZone: tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    const parts = startFmt.formatToParts(start);
    const y = parts.find((p) => p.type === 'year')!.value;
    const m = parts.find((p) => p.type === 'month')!.value;
    const dd = parts.find((p) => p.type === 'day')!.value;
    const nextMidnightMs = new Date(`${y}-${m}-${dd}T00:00:00+10:00`).getTime() + 24 * 3600 * 1000;
    const startMs = start.getTime();
    const endMs = end.getTime();
    const startSideMs = Math.max(0, nextMidnightMs - startMs);
    const endSideMs = Math.max(0, endMs - nextMidnightMs);
    dominantDow = endSideMs > startSideMs ? endDow : startDow;
  }

  if (dominantDow === 0) return { band: 'OT_SUNDAY', spansDayBoundary };
  if (dominantDow === 6) return { band: 'OT_SATURDAY', spansDayBoundary };
  return { band: 'OT_WEEKDAY', spansDayBoundary };
}

const DISTURBANCE_OT_BAND_TO_PENALTY_BAND: Record<DisturbancePayBand, PenaltyBand> = {
  OT_WEEKDAY: 'OVERTIME_FIRST_2H',
  OT_SATURDAY: 'SATURDAY',
  OT_SUNDAY: 'SUNDAY',
  OT_PUBLIC_HOLIDAY: 'PUBLIC_HOLIDAY',
};

const DISTURBANCE_ORDINARY_BAND_TO_PENALTY_BAND: Record<DisturbancePayBand, PenaltyBand> = {
  OT_WEEKDAY: 'WEEKDAY_DAY',
  OT_SATURDAY: 'SATURDAY',
  OT_SUNDAY: 'SUNDAY',
  OT_PUBLIC_HOLIDAY: 'PUBLIC_HOLIDAY',
};

/**
 * Lookup the OT-band multiplier from the rate context. Used on:
 *  - PER_EVENT path (all employment types) — SCHADS cl 25.7(d)(iv) is
 *    OT-rate by definition.
 *  - LESSER_OF_WINDOW path for FT workers (cl 22.9(g)(i) — OT by default).
 *  - LESSER_OF_WINDOW path for PPT/casual workers when the
 *    caller-supplied threshold flag is set (cl 22.9(g)(ii)/(iii)).
 */
function lookupOtMultiplier(
  ctx: RateContext,
  band: DisturbancePayBand,
  employmentType: 'PERMANENT' | 'CASUAL',
  coverageGaps: EbaCoverageGap[],
): number {
  const penaltyBandKey = DISTURBANCE_OT_BAND_TO_PENALTY_BAND[band];
  const fallback =
    band === 'OT_WEEKDAY' ? 1.5 : band === 'OT_SATURDAY' ? 1.5 : band === 'OT_SUNDAY' ? 2.0 : 2.0;
  return resolveRequiredPenalty(ctx, penaltyBandKey, employmentType, fallback, {
    coverageGaps,
  });
}

/**
 * Lookup the ordinary-rate-plus-day-of-week-penalty multiplier for the
 * LESSER_OF_WINDOW path PPT/casual default (cl 22.9(g)(ii)/(iii)). On
 * weekdays returns 1.0× ordinary (+25% casual loading if applicable). On
 * Saturday/Sunday/PH returns the seeded day-of-week penalty band multiplier
 * (which already embeds the casual loading on `award_rate_versions` rows
 * with employmentType=CASUAL per the Aged Care INCLUSIVE convention).
 *
 * Phase 1I.1b simplification: shift-loading interaction (afternoon/night)
 * pending separate clause verification of cl 22.9(g) "applicable shift
 * and weekend penalties" wording. For weekdays this returns ordinary
 * rate only; if industry practice requires shift loading on top, that's
 * tracked in the Phase 2 clause-verification sweep.
 */
function lookupOrdinaryPlusPenaltyMultiplier(
  ctx: RateContext,
  band: DisturbancePayBand,
  employmentType: 'PERMANENT' | 'CASUAL',
  coverageGaps: EbaCoverageGap[],
): number {
  if (band === 'OT_WEEKDAY') {
    return resolveRequiredPenalty(
      ctx,
      DISTURBANCE_ORDINARY_BAND_TO_PENALTY_BAND[band],
      employmentType,
      employmentType === 'CASUAL' ? 1.25 : 1.0,
      { coverageGaps },
    );
  }
  const penaltyBandKey = DISTURBANCE_ORDINARY_BAND_TO_PENALTY_BAND[band];
  // Defaults: Aged Care residential cl 23 (Saturday + Sunday) + cl 29
  // (Public Holiday) ordinary penalty multipliers, casual-loading-inclusive
  // per cl 10.4(b). Phase 1K citation correction (was cl 31.2/31.3/31.4 + cl 11;
  // cl 31 = Community service leave; cl 11 = Termination of employment).
  const fallback =
    band === 'OT_SATURDAY'
      ? employmentType === 'CASUAL'
        ? 1.75
        : 1.5
      : band === 'OT_SUNDAY'
        ? employmentType === 'CASUAL'
          ? 2.0
          : 1.75
        : employmentType === 'CASUAL'
          ? 2.75
          : 2.5;
  return resolveRequiredPenalty(ctx, penaltyBandKey, employmentType, fallback, {
    coverageGaps,
  });
}

function applyMinimumAndRound(actualHours: number, minimumHours: number, roundingMinutes: number) {
  if (actualHours <= minimumHours) return minimumHours;
  const incrementHours = roundingMinutes / 60;
  const over = actualHours - minimumHours;
  const roundedOver = Math.ceil(over / incrementHours) * incrementHours;
  return minimumHours + roundedOver;
}

// Half-cent rounding (2026-10-08): exact half-up roundMoney; recorded under each
// consuming engine's ENGINE_VERSION bump (MA000100 5.9.23, MA000018 5.7.44,
// MA000034 5.8.24, MA000027 5.7.15).
function round2(n: number): number {
  return roundMoney(n);
}

function round4(n: number): number {
  return Math.round(n * 10000) / 10000;
}

/* ═══════════════════════════════════════════════════════════════════════
 * PER_EVENT path (SCHADS MA000100 cl 25.7(d)(iv))
 * ═══════════════════════════════════════════════════════════════════════ */

function calculatePerEvent(
  input: PerEventDisturbanceInput,
  rules: DisturbanceRules,
  ctx: RateContext,
  baseHourlyRate: number,
  employmentType: 'PERMANENT' | 'CASUAL',
): DisturbancePayBreakdown {
  const warnings: string[] = [];
  const coverageGaps: EbaCoverageGap[] = [];
  const tz = input.timezone ?? 'Australia/Sydney';

  const actualMs = input.disturbanceEndedAt.getTime() - input.disturbanceStartedAt.getTime();
  const actual = Math.max(0, actualMs / (1000 * 60 * 60));
  if (actual <= 0) {
    warnings.push('Disturbance end is not after start — paying minimum-only.');
  }

  const { band, spansDayBoundary } = selectBand(
    input.disturbanceStartedAt,
    input.disturbanceEndedAt,
    tz,
    input.isPublicHoliday ?? false,
  );
  const multiplier = lookupOtMultiplier(ctx, band, employmentType, coverageGaps);

  const paidHours = applyMinimumAndRound(actual, rules.minimumHours, rules.roundingMinutes);
  const paidAmount = round2(paidHours * baseHourlyRate * multiplier);

  if (spansDayBoundary) {
    warnings.push(
      `Disturbance spans a day-type boundary. Dominant day-type by paid minutes drives the OT band (${band}).`,
    );
  }

  return {
    paidHours: round4(paidHours),
    paidAmount,
    bandApplied: band,
    multiplierApplied: multiplier,
    clauseRef: rules.clauseRef,
    spansDayBoundary,
    model: 'PER_EVENT',
    ...coverageGapsField(coverageGaps),
    warnings,
  };
}

/* ═══════════════════════════════════════════════════════════════════════
 * LESSER_OF_WINDOW path (Aged Care MA000018 cl 22.9(f) + (g))
 * ═══════════════════════════════════════════════════════════════════════ */

function calculateLesserOfWindow(
  input: LesserOfWindowDisturbanceInput,
  rules: DisturbanceRules,
  ctx: RateContext,
  baseHourlyRate: number,
  employmentType: 'PERMANENT' | 'CASUAL',
  options: DisturbancePayOptions,
): DisturbancePayBreakdown {
  const warnings: string[] = [];
  const coverageGaps: EbaCoverageGap[] = [];
  const tz = input.timezone ?? 'Australia/Sydney';

  if (input.workEpisodes.length === 0) {
    warnings.push(
      'LESSER_OF_WINDOW invoked with zero work episodes — no cl 22.9(f) pay applies (sleepover allowance handled separately by caller).',
    );
    return {
      paidHours: 0,
      paidAmount: 0,
      bandApplied: 'OT_WEEKDAY',
      multiplierApplied: 0,
      clauseRef: rules.clauseRef,
      spansDayBoundary: false,
      workEpisodeCount: 0,
      model: 'LESSER_OF_WINDOW',
      warnings,
    };
  }

  // cl 22.9(f): paidTime = min(
  //   sleepoverStart → max(workEnd),
  //   min(workStart) → sleepoverEnd
  // )
  const firstWorkStart = input.workEpisodes.reduce(
    (min, ep) => (ep.startedAt.getTime() < min.getTime() ? ep.startedAt : min),
    input.workEpisodes[0]!.startedAt,
  );
  const lastWorkEnd = input.workEpisodes.reduce(
    (max, ep) => (ep.endedAt.getTime() > max.getTime() ? ep.endedAt : max),
    input.workEpisodes[0]!.endedAt,
  );

  const windowAMs = lastWorkEnd.getTime() - input.sleepoverStart.getTime();
  const windowBMs = input.sleepoverEnd.getTime() - firstWorkStart.getTime();
  const paidMs = Math.max(0, Math.min(windowAMs, windowBMs));
  const paidHours = paidMs / (1000 * 60 * 60);

  // The dominant day-type is selected across the PAID WINDOW, not the
  // raw disturbance episode. The window starts at the earlier of
  // (sleepoverStart, firstWorkStart) and ends at the earlier of
  // (lastWorkEnd, sleepoverEnd) — i.e. the actual span of paid time.
  const windowStart = windowAMs <= windowBMs ? input.sleepoverStart : firstWorkStart;
  const windowEnd = windowAMs <= windowBMs ? lastWorkEnd : input.sleepoverEnd;

  const { band, spansDayBoundary } = selectBand(
    windowStart,
    windowEnd,
    tz,
    input.isPublicHoliday ?? false,
  );

  // cl 22.9(g) rate selection. Default to FT (OT) when no subtype passed —
  // permissive default to keep tests/legacy callers working, but caller
  // (snapshotPayRun) should always supply the subtype for Aged Care.
  const subtype: DisturbanceEmploymentSubtype =
    options.employmentSubtype ?? (employmentType === 'CASUAL' ? 'CASUAL' : 'FULL_TIME');

  let multiplier: number;
  let rateRationale: string;

  if (subtype === 'FULL_TIME') {
    multiplier = lookupOtMultiplier(ctx, band, employmentType, coverageGaps);
    rateRationale = `cl 22.9(g)(i) FT → OT rate on ${band}`;
  } else if (subtype === 'PART_TIME') {
    if (options.pptOvertimeApplies) {
      multiplier = lookupOtMultiplier(ctx, band, employmentType, coverageGaps);
      rateRationale = `cl 22.9(g)(ii) PPT threshold exceeded → OT rate on ${band}`;
    } else {
      multiplier = lookupOrdinaryPlusPenaltyMultiplier(ctx, band, employmentType, coverageGaps);
      rateRationale = `cl 22.9(g)(ii) PPT default → ordinary + day-of-week penalty on ${band}`;
    }
  } else {
    // CASUAL
    if (options.casualOvertimeApplies) {
      multiplier = lookupOtMultiplier(ctx, band, employmentType, coverageGaps);
      rateRationale = `cl 22.9(g)(iii) casual 38h/76h threshold exceeded → OT rate on ${band}`;
    } else {
      multiplier = lookupOrdinaryPlusPenaltyMultiplier(ctx, band, employmentType, coverageGaps);
      rateRationale = `cl 22.9(g)(iii) casual default → ordinary + day-of-week penalty + 25% loading on ${band}`;
    }
  }

  const paidAmount = round2(paidHours * baseHourlyRate * multiplier);

  if (spansDayBoundary) {
    warnings.push(
      `Paid window spans a day-type boundary. Dominant day-type by paid minutes drives band selection (${band}).`,
    );
  }
  if (!options.employmentSubtype) {
    warnings.push(
      `LESSER_OF_WINDOW invoked without employmentSubtype — defaulted to ${subtype}. Caller should supply employmentSubtype for cl 22.9(g) rate selection.`,
    );
  }

  return {
    paidHours: round4(paidHours),
    paidAmount,
    bandApplied: band,
    multiplierApplied: multiplier,
    clauseRef: rules.clauseRef,
    spansDayBoundary,
    rateRationale,
    workEpisodeCount: input.workEpisodes.length,
    model: 'LESSER_OF_WINDOW',
    ...coverageGapsField(coverageGaps),
    warnings,
  };
}

/* ═══════════════════════════════════════════════════════════════════════
 * Public API
 * ═══════════════════════════════════════════════════════════════════════ */

/**
 * Compute disturbance pay per the rules' structural model.
 *
 * PER_EVENT (SCHADS): one disturbance event per call. Caller sums for
 * multi-disturbance sleepovers.
 *
 * LESSER_OF_WINDOW (Aged Care): ONE call per sleepover, passing all
 * disturbances within the sleepover via `workEpisodes`. The engine
 * collapses per cl 22.9(f).
 *
 * The `options` parameter is required for LESSER_OF_WINDOW rate selection
 * (cl 22.9(g) employment subtype + threshold flags). Ignored for PER_EVENT.
 */
export function calculateDisturbancePay(
  input: DisturbancePayInput,
  rules: DisturbanceRules,
  ctx: RateContext,
  baseHourlyRate: number,
  employmentType: 'PERMANENT' | 'CASUAL',
  options: DisturbancePayOptions = {},
): DisturbancePayBreakdown {
  if (rules.model === 'LESSER_OF_WINDOW') {
    if (!isLesserOfWindowInput(input)) {
      // Caller supplied a PER_EVENT shape under a LESSER_OF_WINDOW rule
      // set. Refuse to fall back silently — this is a regulated calc and
      // a model mismatch is a contract violation. Return zero + warning.
      return {
        paidHours: 0,
        paidAmount: 0,
        bandApplied: 'OT_WEEKDAY',
        multiplierApplied: 0,
        clauseRef: rules.clauseRef,
        spansDayBoundary: false,
        model: 'LESSER_OF_WINDOW',
        warnings: [
          `LESSER_OF_WINDOW model requires input.model='LESSER_OF_WINDOW' with sleepoverStart, sleepoverEnd, workEpisodes. Caller passed PER_EVENT shape — refusing to fall back. Returning zero pay; check caller is grouping disturbances per sleepover and passing the full window.`,
        ],
      };
    }
    return calculateLesserOfWindow(input, rules, ctx, baseHourlyRate, employmentType, options);
  }

  // PER_EVENT path. Accept either shape; LesserOfWindow shape is an error
  // here too (model mismatch in the other direction).
  if (isLesserOfWindowInput(input)) {
    return {
      paidHours: 0,
      paidAmount: 0,
      bandApplied: 'OT_WEEKDAY',
      multiplierApplied: 0,
      clauseRef: rules.clauseRef,
      spansDayBoundary: false,
      model: 'PER_EVENT',
      warnings: [
        `PER_EVENT model requires input with disturbanceStartedAt/disturbanceEndedAt. Caller passed LESSER_OF_WINDOW shape — refusing to compute. Returning zero pay.`,
      ],
    };
  }
  return calculatePerEvent(input, rules, ctx, baseHourlyRate, employmentType);
}

function isLesserOfWindowInput(
  input: DisturbancePayInput,
): input is LesserOfWindowDisturbanceInput {
  return (input as LesserOfWindowDisturbanceInput).model === 'LESSER_OF_WINDOW';
}
